<template>
	<div>
		<v-toolbar-title tabindex="0" class="app-title"> {{ t('navigation_planning') }} </v-toolbar-title>
		<v-row class="mt-1" justify="space-between">
			<v-col cols="2">
				<v-text-field
					v-model="searchText"
					append-inner-icon="mdi-magnify"
					density="compact"
					:label="$t('action_search')"
					type="text"
					variant="outlined"
					clearable
					hide-details
					class="mb-2 planning-input-dense"
					@update:model-value="handleSearch"
				></v-text-field>
			</v-col>
			<v-col cols="10">
				<v-row justify="end">
					<v-col cols="auto">
						<v-btn flat @click="clearFilters">{{ $t('performance_metrics.btn_clear') }}</v-btn>
					</v-col>
					<v-col cols="auto">
						<DateRangePicker
							v-model="dateRange"
							:published-range="publishedDateRange"
							:planning-range="planningDateRange"
							:allow-preset-editing="true"
							@change="handleDateChange"
						/>
					</v-col>
					<v-col :cols="isCurrentLocalGerman ? '3' : '2'">
						<CustomDropDown
							v-model="violationFilter"
							:label="$t('core_chart_violations')"
							:options="violationOptions"
							:tooltip="false"
							@apply="handleViolationChange"
						/>
					</v-col>
					<v-col cols="2">
						<MultiSelectDropDown
							v-model="selectedUserGroups"
							:label="$t('menu_groups')"
							:options="filterMenuOptions"
							:tooltip="true"
							@apply="updateUserGroupFilters"
						/>
					</v-col>
					<v-col cols="auto">
						<v-tooltip v-model="showUnallocatedTooltip" open-delay="500" :text="t('tooltips.filterUnallocatedDrivers')" location="top">
							<template v-slot:activator="{ props }">
								<IconButton
									v-if="!showUnallocatedDrivers"
									v-bind="props"
									:icon="'mdi-developer-board'"
									:iconColor="'black'"
									buttonSize="32"
									size="24"
									@click="handleFilterUnallocatedDrivers"
								/>

								<v-btn v-else v-bind="props" icon variant="text" density="comfortable" @click="handleClearUnallocatedDriverFilter">
									<v-img :src="developerBoardOff" width="24" height="24" />
								</v-btn>
							</template>
						</v-tooltip>
					</v-col>
					<v-col cols="auto">
						<v-tooltip
							v-model="showDeletedDutiesTooltip"
							open-delay="500"
							:text="isActive ? t('tooltips.restoreDeletedDutiesDisabledPreview') : t('tooltips.restoreDeletedDuties')"
							location="top"
						>
							<template v-slot:activator="{ props }">
								<span v-bind="props" class="d-inline-block">
									<IconButton
										:icon="'mdi-delete-restore'"
										:iconColor="isActive ? 'grey' : 'black'"
										buttonSize="32"
										size="24"
										:disabled="isActive"
										@click="openDeletedDutiesDialog"
									/>
								</span>
							</template>
						</v-tooltip>
					</v-col>
					<v-divider vertical class="mt-3" length="32" />
					<v-col v-if="!isActive" cols="auto">
						<v-tooltip
							v-model="showPreviewTooltip"
							:text="t('planning_preview_mode')"
							location="top"
							open-on-hover
							:open-on-click="false"
							:open-on-focus="false"
							open-delay="500"
						>
							<template #activator="{ props }">
								<v-btn
									v-bind="props"
									variant="flat"
									color="#F5F5F5"
									height="32"
									min-width="0"
									class="px-2"
									:aria-label="t('planning_preview_mode')"
									@click="onPreviewClick"
								>
									<v-img contain :src="previewSVG" alt="" width="24" height="24" />
								</v-btn>
							</template>
						</v-tooltip>
					</v-col>
					<v-col cols="auto">
						<v-tooltip v-model="showDownloadTooltip" :disabled="showDownloadMenu" :text="t('tooltips.download')" location="top" open-delay="500">
							<template #activator="{ props: tooltipProps }">
								<v-menu v-model="showDownloadMenu">
									<template #activator="{ props: menuProps }">
										<IconButton
											v-bind="{ ...tooltipProps, ...menuProps }"
											:icon="'mdi-download'"
											iconColor="black"
											buttonSize="32"
											size="24"
											:loading="isDownloading"
										/>
									</template>
									<v-list density="compact">
										<v-list-item @click="handleDownload('csv')">
											<v-list-item-title>{{ t('download_as_csv') }}</v-list-item-title>
										</v-list-item>
										<v-list-item @click="handleDownload('xlsx')">
											<v-list-item-title>{{ t('download_as_xlsx') }}</v-list-item-title>
										</v-list-item>
									</v-list>
								</v-menu>
							</template>
						</v-tooltip>
					</v-col>
					<v-col v-if="isActive" cols="auto">
						<v-tooltip location="top">
							<template #activator="{ props }">
								<v-btn
									v-bind="props"
									variant="flat"
									:color="showRightPanel ? '#10565C' : '#f5f5f5'"
									size="32"
									min-width="0"
									class="mr-2"
									@click="togglePreviewPanel"
								>
									<svg width="32" height="32" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
										<path
											d="M16 12.0156H17.5V16.2344L21.0156 18.3438L20.2656 19.5625L16 16.9844V12.0156ZM10.6094 9.625C12.3906 7.875 14.5156 7 16.9844 7C19.4531 7 21.5625 7.875 23.3125 9.625C25.0938 11.375 25.9844 13.5 25.9844 16C25.9844 18.5 25.0938 20.625 23.3125 22.375C21.5625 24.125 19.4531 25 16.9844 25C15.9531 25 14.8125 24.75 13.5625 24.25C12.3438 23.7188 11.375 23.0938 10.6562 22.375L12.0625 20.9219C13.4375 22.2969 15.0781 22.9844 16.9844 22.9844C18.9219 22.9844 20.5781 22.3125 21.9531 20.9688C23.3281 19.5938 24.0156 17.9375 24.0156 16C24.0156 14.0625 23.3281 12.4219 21.9531 11.0781C20.5781 9.70312 18.9219 9.01562 16.9844 9.01562C15.0469 9.01562 13.3906 9.70312 12.0156 11.0781C10.6719 12.4219 10 14.0625 10 16H13L8.96875 20.0312L8.875 19.8906L4.98438 16H7.98438C7.98438 13.5 8.85938 11.375 10.6094 9.625Z"
											:fill="showRightPanel ? '#FFFFFF' : '#000000DE'"
										/>
									</svg>
								</v-btn>
							</template>

							<span>{{ showRightPanel ? $t('preview_close_panel') : $t('preview_open_panel') }}</span>
						</v-tooltip>
					</v-col>
				</v-row>
			</v-col>
		</v-row>
	</div>

	<DeletedDutiesDialog v-model="showDeletedDutiesDialog" />
</template>

<script setup lang="ts">
import { ref, computed, watch, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { storeToRefs } from 'pinia';

import CustomDropDown from '../Controls/CustomDropDown.vue';
import MultiSelectDropDown from '../Controls/MultiSelectDropDown.vue';
import DateRangePicker from '../../../shared/components/DateRangePicker.vue';
import IconButton from '../Controls/IconButton.vue';
import DeletedDutiesDialog from './Modals/DeletedDutiesDialog.vue';
import { STORAGE_KEYS } from '../../constants';
import type { ApiDriverUser } from '../../types/api/users-response';
import { useDutyChangePreview } from '@/rennovation/planning-view/composables/useDutyChangePreview';
import { VIOLATION_OPTIONS } from '../../constants';
import type { OptionItem, FilterValue } from '@/rennovation/planning-view/types/components/types';
import type { DateRange } from '../../../shared/types/date-range-picker';
import fileDownloadMixin from '@/mixins/fileDownloadMixin';
import previewSVG from '@/assets/preview-mode.svg';
const { downloadFile } = fileDownloadMixin.methods;

// Pinia Stores & Bridge
import { useUserGroupBridge } from '@/rennovation/planning-view/store/useUserGroupBridge';
import { usePlanningStore } from '@/rennovation/planning-view/store/usePlanningStore';
import { useRosterStore } from '@/rennovation/planning-view/store/useRosterStore';
import { useDayStore } from '../../store/useDayStore';
import { useUserBridgeStore } from '@/rennovation/planning-view/store/userBridge';
import developerBoardOff from '@/assets/developer-board-off.svg';

const { t } = useI18n();
const userGroupStore = useUserGroupBridge();
const { userGroups } = storeToRefs(userGroupStore);
const { isActive, showRightPanel, handlePreviewButtonClick, togglePreviewPanel } = useDutyChangePreview();
const rosterStore = useRosterStore();
const planningStore = usePlanningStore();
const dayStore = useDayStore();
const isDownloading = ref(false);

const showUnallocatedTooltip = ref(false);
const showDeletedDutiesTooltip = ref(false);
const showDownloadTooltip = ref(false);
const showDownloadMenu = ref(false);
const showPreviewTooltip = ref(false);

const emit = defineEmits<{
	(e: 'update:dateRange', range: DateRange): void;
	(e: 'update:search', text: string): void;
	(e: 'update:violation', params: any): void;
	(e: 'update:group', params: any): void;
	(e: 'toggle-unallocated'): void;
	(e: 'apply-filters', payload: any): void;
	(e: 'clear-filters'): void;
}>();

const dateRange = defineModel<DateRange>('dateRange', {
	required: true,
});

const showDeletedDutiesDialog = ref(false);
const searchText = ref('');
const showUnallocatedDrivers = ref(false);

const violationFilter = ref({
	status: '',
	selected: [] as string[],
});

const selectedUserGroups = ref<FilterValue>({
	selected: [],
});

const violationOptions = VIOLATION_OPTIONS;

const isCurrentLocalGerman = localStorage.getItem('locale') === 'de-CH';

const onPreviewClick = () => {
	showPreviewTooltip.value = false;
	handlePreviewButtonClick();
};

const publishedDateRange = computed(() => ({
	startDate: dayStore.publishedDayRange?.from ? new Date(dayStore.publishedDayRange.from) : null,
	endDate: dayStore.publishedDayRange?.to ? new Date(dayStore.publishedDayRange.to) : null,
}));

const planningDateRange = computed(() => ({
	startDate: dayStore.planningDayRange?.from ? new Date(dayStore.planningDayRange.from) : null,
	endDate: dayStore.planningDayRange?.to ? new Date(dayStore.planningDayRange.to) : null,
}));

const sortUserGroups = computed(() => {
	const list = [...userGroups.value];

	const savedSortOrder = localStorage.getItem('userGroupSortOrder');
	if (savedSortOrder && savedSortOrder !== 'custom') {
		const pinAll = (a: any, b: any) => Number(a.name === 'All') - Number(b.name === 'All');
		if (savedSortOrder === 'asc') {
			list.sort((a, b) => pinAll(b, a) || a.name.localeCompare(b.name));
		} else if (savedSortOrder === 'desc') {
			list.sort((a, b) => pinAll(b, a) || b.name.localeCompare(a.name));
		}
		return list;
	}

	const sortOrder = useUserBridgeStore().user?.userGroupSortOrder;
	if (!Array.isArray(sortOrder) || !sortOrder.length) {
		return list;
	}

	const orderMap = new Map(sortOrder.map((id: string, index: number) => [id, index]));
	list.sort((a, b) => {
		const indexA = orderMap.has(a._id) ? orderMap.get(a._id)! : Infinity;
		const indexB = orderMap.has(b._id) ? orderMap.get(b._id)! : Infinity;
		return indexA - indexB;
	});

	return list;
});

const filterMenuOptions = computed(() => {
	const sortedGroups = sortUserGroups.value.filter((group: any) => !group.deleted);

	return sortedGroups.map((group: any) => ({
		label: group.name,
		value: group.id || group._id,
		children:
			group.users
				?.slice()
				.sort((a: any, b: any) => (a.sortName || '').localeCompare(b.sortName || ''))
				.map((user: any) => ({
					label: user.sortName,
					value: user.id || user._id,
				})) || [],
	}));
});

// Simply toggle localStorage state and emit event to Parent
const handleFilterUnallocatedDrivers = () => {
	showUnallocatedTooltip.value = false;
	showUnallocatedDrivers.value = true;
	localStorage.setItem(STORAGE_KEYS.SHOW_UNALLOCATED_DRIVERS, JSON.stringify(true));
	emit('toggle-unallocated');
};

const handleClearUnallocatedDriverFilter = () => {
	showUnallocatedTooltip.value = false;
	showUnallocatedDrivers.value = false;
	localStorage.setItem(STORAGE_KEYS.SHOW_UNALLOCATED_DRIVERS, JSON.stringify(false));
	emit('toggle-unallocated');
};

const updateUserGroupFilters = () => {
	planningStore.filters.selectedUserGroups = selectedUserGroups.value;
	emit('update:group', selectedUserGroups.value);
};

const openDeletedDutiesDialog = () => {
	if (isActive.value) return;
	showDeletedDutiesTooltip.value = false;
	showDeletedDutiesDialog.value = true;
};

onMounted(() => {
	searchText.value = planningStore.filters.searchQuery ?? '';
	showUnallocatedDrivers.value = JSON.parse(localStorage.getItem(STORAGE_KEYS.SHOW_UNALLOCATED_DRIVERS) ?? 'false');
	const violations = planningStore.filters.violations;
	if (violations) {
		const status = Object.keys(violations).find((key) => key !== 'category' && violations[key]);
		violationFilter.value = {
			status: status ?? '',
			selected: Object.keys(violations.category ?? {}).filter((key) => violations.category[key]),
		};
	}
});

watch(
	filterMenuOptions,
	(options) => {
		if (!options.length) return;
		const savedGroups = selectedUserGroups.value.selected?.length
			? selectedUserGroups.value.selected
			: (planningStore.filters.selectedUserGroups?.selected ?? []);

		selectedUserGroups.value = {
			selected: savedGroups.map((savedGroup) => options.find((group) => group.value === savedGroup.value)).filter(Boolean) as OptionItem[],
		};
	},
	{ immediate: true }
);

watch(isActive, (active) => {
	if (active) showDeletedDutiesDialog.value = false;
});

const handleDateChange = (range: DateRange) => {
	emit('update:dateRange', range);
};
let debounceTimeout: NodeJS.Timeout;
const handleSearch = (value: string) => {
	clearTimeout(debounceTimeout);

	debounceTimeout = setTimeout(() => {
		planningStore.filters.searchQuery = value;
		emit('update:search', value);
	}, 350);
};

const handleViolationChange = () => {
	planningStore.filters.searchQuery = searchText.value;
	const { status, selected } = violationFilter.value;
	if (status && selected.length > 0) {
		planningStore.filters.violations = {
			[status]: true,
			category: selected.reduce((acc: Record<string, boolean>, key) => {
				acc[key] = true;
				return acc;
			}, {}),
		};
	} else {
		planningStore.filters.violations = null;
	}

	emit('update:violation', planningStore.filters.violations);
};

const handleDownload = async (format: 'csv' | 'xlsx') => {
	showDownloadMenu.value = false;
	try {
		isDownloading.value = true;

		const { fromDate: from, toDate: to, userStatus, filteredDrivers, searchQuery } = planningStore.filters;
		const allowedIds = filteredDrivers;
		const filteredSet = allowedIds?.length ? new Set(allowedIds) : null;

		const isFiltered = (user: ApiDriverUser) => {
			if (!filteredSet) return true;
			return filteredSet.has(user.id || (user as any)._id);
		};

		let driverIds;
		if (userStatus === 'active' && !searchQuery) {
			driverIds = planningStore.activeUsers
				.filter(isFiltered)
				.map((driver) => driver.id || (driver as any)._id)
				.filter(Boolean);
		} else if (userStatus === 'inactive' && !searchQuery) {
			driverIds = (planningStore.inactiveUsers || [])
				.filter(isFiltered)
				.map((driver) => driver.id || (driver as any)._id)
				.filter(Boolean);
		} else if (searchQuery) {
			driverIds = planningStore.filters.combinedFilteredDrivers;
		} else {
			driverIds = filteredDrivers;
		}

		const fileName = `duty-roster-${from}-to-${to}.${format}`;
		if (!driverIds) {
			isDownloading.value = false;
			return;
		}
		if (format === 'csv') {
			const csv = await rosterStore.exportRosterCsv({ from, to, separator: ';', drivers: driverIds });
			if (csv !== null) {
				downloadFile(csv, fileName);
			}
		} else {
			const xlsx = await rosterStore.exportRosterXlsx({ from, to, drivers: driverIds });
			if (xlsx !== null) {
				downloadFile(xlsx, fileName);
			}
		}
	} finally {
		isDownloading.value = false;
	}
};
const clearFilters = () => {
	showUnallocatedDrivers.value = false;
	violationFilter.value = {
		status: '',
		selected: [],
	};
	selectedUserGroups.value = {
		selected: [],
	};
	searchText.value = '';
	emit('clear-filters');
};
</script>

<style scoped>
.app-title {
	font-family: 'Roboto', sans-serif;
	font-weight: 400;
	font-size: 34px;
	line-height: 40px;
	letter-spacing: 0.25px;
}

.planning-input-dense :deep(.v-field__input) {
	min-height: 32px;
	font-size: 0.75rem;
}
.toggle-preview-icon {
	width: 32px;
	height: 32px;
	background-color: #000000de;
	-webkit-mask-image: v-bind('`url(${togglePreviewSVG})`');
	mask-image: v-bind('`url(${togglePreviewSVG})`');
	-webkit-mask-size: contain;
	mask-size: contain;
	-webkit-mask-repeat: no-repeat;
	mask-repeat: no-repeat;
	-webkit-mask-position: center;
	mask-position: center;
}
.toggle-preview-icon.is-active {
	background-color: #ffffff;
}
</style>
